/**
 * Date Range Picker Component
 * Version: 6.2.0
 * 
 * Timeframe filters for analytics
 */
import { useState } from 'react'
import './DateRangePicker.css'

// ============================================
// Quick Filters
// ============================================
type QuickFilter = '1D' | '1W' | '1M' | '3M' | '6M' | 'YTD' | '1Y' | 'ALL' | 'CUSTOM'

interface QuickFiltersProps {
  value: QuickFilter
  onChange: (filter: QuickFilter) => void
}

function QuickFilters({ value, onChange }: QuickFiltersProps) {
  const filters: QuickFilter[] = ['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', 'ALL']
  
  return (
    <div className="quick-filters">
      {filters.map((filter) => (
        <button
          key={filter}
          className={`quick-filter-btn ${value === filter ? 'active' : ''}`}
          onClick={() => onChange(filter)}
        >
          {filter}
        </button>
      ))}
    </div>
  )
}

// ============================================
// Custom Date Picker
// ============================================
interface CustomDatePickerProps {
  startDate: Date
  endDate: Date
  onStartChange: (date: Date) => void
  onEndChange: (date: Date) => void
  onApply: () => void
  onCancel: () => void
}

function CustomDatePicker({ 
  startDate, 
  endDate, 
  onStartChange, 
  onEndChange,
  onApply,
  onCancel
}: CustomDatePickerProps) {
  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0]
  }

  return (
    <div className="custom-date-picker">
      <div className="date-inputs">
        <div className="date-input-group">
          <label>From</label>
          <input 
            type="date"
            value={formatDate(startDate)}
            onChange={(e) => onStartChange(new Date(e.target.value))}
            max={formatDate(endDate)}
          />
        </div>
        <span className="date-separator">→</span>
        <div className="date-input-group">
          <label>To</label>
          <input 
            type="date"
            value={formatDate(endDate)}
            onChange={(e) => onEndChange(new Date(e.target.value))}
            min={formatDate(startDate)}
          />
        </div>
      </div>
      <div className="date-actions">
        <button className="date-cancel-btn" onClick={onCancel}>Cancel</button>
        <button className="date-apply-btn" onClick={onApply}>Apply</button>
      </div>
    </div>
  )
}

// ============================================
// Preset Ranges
// ============================================
interface PresetRange {
  label: string
  getDates: () => { start: Date; end: Date }
}

const presetRanges: PresetRange[] = [
  {
    label: 'Today',
    getDates: () => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      return { start: today, end }
    }
  },
  {
    label: 'Yesterday',
    getDates: () => {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0)
      const endYesterday = new Date(yesterday)
      endYesterday.setHours(23, 59, 59, 999)
      return { start: yesterday, end: endYesterday }
    }
  },
  {
    label: 'Last 7 Days',
    getDates: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      return { start, end }
    }
  },
  {
    label: 'Last 30 Days',
    getDates: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      return { start, end }
    }
  },
  {
    label: 'This Week',
    getDates: () => {
      const today = new Date()
      const day = today.getDay()
      const start = new Date(today)
      start.setDate(today.getDate() - day)
      start.setHours(0, 0, 0, 0)
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      return { start, end }
    }
  },
  {
    label: 'This Month',
    getDates: () => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      return { start, end }
    }
  },
  {
    label: 'Last Month',
    getDates: () => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const end = new Date(today.getFullYear(), today.getMonth(), 0)
      end.setHours(23, 59, 59, 999)
      return { start, end }
    }
  },
  {
    label: 'This Quarter',
    getDates: () => {
      const today = new Date()
      const quarter = Math.floor(today.getMonth() / 3)
      const start = new Date(today.getFullYear(), quarter * 3, 1)
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      return { start, end }
    }
  }
]

// ============================================
// Main Date Range Picker Component
// ============================================
interface DateRangePickerProps {
  onRangeChange: (start: Date, end: Date) => void
  initialRange?: { start: Date; end: Date }
}

function DateRangePicker({ onRangeChange, initialRange }: DateRangePickerProps) {
  const [selectedFilter, setSelectedFilter] = useState<QuickFilter>('1M')
  const [showCustom, setShowCustom] = useState(false)
  const [customStart, setCustomStart] = useState<Date>(
    initialRange?.start || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  )
  const [customEnd, setCustomEnd] = useState<Date>(
    initialRange?.end || new Date()
  )
  const [showPresets, setShowPresets] = useState(false)

  const handleFilterChange = (filter: QuickFilter) => {
    setSelectedFilter(filter)
    setShowCustom(false)

    if (filter === 'ALL') {
      onRangeChange(new Date(2020, 0, 1), new Date())
      return
    }

    const end = new Date()
    let start: Date

    switch (filter) {
      case '1D':
        start = new Date()
        start.setDate(start.getDate() - 1)
        break
      case '1W':
        start = new Date()
        start.setDate(start.getDate() - 7)
        break
      case '1M':
        start = new Date()
        start.setMonth(start.getMonth() - 1)
        break
      case '3M':
        start = new Date()
        start.setMonth(start.getMonth() - 3)
        break
      case '6M':
        start = new Date()
        start.setMonth(start.getMonth() - 6)
        break
      case 'YTD':
        start = new Date(end.getFullYear(), 0, 1)
        break
      case '1Y':
        start = new Date()
        start.setFullYear(start.getFullYear() - 1)
        break
      default:
        start = new Date()
        start.setMonth(start.getMonth() - 1)
    }

    onRangeChange(start, end)
  }

  const handleCustomApply = () => {
    setSelectedFilter('CUSTOM')
    onRangeChange(customStart, customEnd)
    setShowCustom(false)
  }

  const handlePresetSelect = (preset: PresetRange) => {
    const { start, end } = preset.getDates()
    setCustomStart(start)
    setCustomEnd(end)
    setSelectedFilter('CUSTOM')
    onRangeChange(start, end)
    setShowPresets(false)
  }

  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    })
  }

  return (
    <div className="date-range-picker">
      <div className="date-range-header">
        <QuickFilters value={selectedFilter} onChange={handleFilterChange} />
        <button 
          className={`custom-btn ${showCustom || selectedFilter === 'CUSTOM' ? 'active' : ''}`}
          onClick={() => setShowPresets(!showPresets)}
        >
          📅 Custom
        </button>
      </div>

      {showPresets && (
        <div className="presets-dropdown">
          <div className="presets-header">
            <span>Quick Presets</span>
          </div>
          <div className="presets-list">
            {presetRanges.map((preset) => (
              <button
                key={preset.label}
                className="preset-btn"
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showCustom && (
        <div className="custom-range-panel">
          <CustomDatePicker
            startDate={customStart}
            endDate={customEnd}
            onStartChange={setCustomStart}
            onEndChange={setCustomEnd}
            onApply={handleCustomApply}
            onCancel={() => setShowCustom(false)}
          />
        </div>
      )}

      <div className="date-range-display">
        <span className="range-label">Selected:</span>
        <span className="range-dates">
          {formatDisplayDate(customStart)} → {formatDisplayDate(customEnd)}
        </span>
      </div>
    </div>
  )
}

// ============================================
// Export
// ============================================
export { DateRangePicker, QuickFilters, CustomDatePicker }

export type { QuickFilter, DateRangePickerProps }